<template lang="pug">
  .quick-response
    h3.quick-response_title

    search-and-create-box(:new-box-placeholder="$t('conversation_tab.side_panel.quick_reponses_accordion.new_quick_response')"
          :search-box-placeholder="$t('conversation_tab.side_panel.quick_reponses_accordion.search') "
          :active-toggle-name="$t('conversation_tab.side_panel.quick_reponses_accordion.pin')"
          :inactive-toggle-name="$t('conversation_tab.side_panel.quick_reponses_accordion.unpin')"
          :add-tooltip="$t('conversation_tab.side_panel.quick_reponses_accordion.add_quick_response_tooltip')"
          :should-use-parent-create="true"
          create-box-type="textarea"
          toggle-icon="thumbtack"
          :advanced="true"
          :rows="3"
          @search="onSearchChange"
          @create="createResponse")

    aiq-collapse.quick-response(v-model="selectedCollapse")
      aiq-collapse-item(v-if="canView('/conversations/right_panel/quick_responses/mine')"
                        name="mine"
                        :title="`${$t('conversation_tab.side_panel.quick_reponses_accordion.mine')} (${myQuickResponsesPagination.rowCount})`")
        .quick-response_list
          quick-response-item(v-for="item in myQuickResponses",
                              :response="item",
                              :key="item.index",
                              @editMode="editResponseItem",
                              @select="selectResponse"
                              @pin="pinResponse"
                              @unpin="unpinResponse"
                              @remove="deleteResponse")
        infinite-loading(ref="myInfiniteLoading"
                        v-if="doMyInfiniteScroll"
                        class="text-center"
                        :distance="10"
                        @infinite="onMyScrollLoad")
      aiq-collapse-item(v-if="canView('/conversations/right_panel/quick_responses/team')"
                        name="team"
                        :title="`${$t('conversation_tab.side_panel.quick_reponses_accordion.team')} (${teamQuickResponsesPagination.rowCount})`")
        .quick-response_list
          aiq-select(v-model="selectedTeam" size="small" value-key="id" name="teams" :placeholder="$t('conversation_tab.side_panel.quick_reponses_accordion.select_team')" @change="loadTeamQuickResponses()" clearable)
            aiq-option(v-for="item in agentProfile.teams" :key="item.id" :label="item.name" :value="item")
          quick-response-item(v-for="item in teamQuickResponses",
                            :response="item",
                            :key="item.index"
                            :read-only="true"
                            @add-to-mine="addToMineResponses"
                            @select="selectResponse")
        infinite-loading(ref="teamInfiniteLoading"
                        v-if="doTeamInfiniteScroll"
                        class="text-center"
                        :distance="10"
                        @infinite="onTeamScrollLoad")
  accordion-item-editor(v-if="isEditorVisible"
                        :item="selectedQuickResponseItem"
                        :is-visible="isEditorVisible"
                        toggle-property="is_pinned"
                        base-translation-path="settings_tab.admin_tab.quick_response_tab"
                        @save="updateResponse"
                        @remove="deleteResponse"
                        @close="closeEditor")
</template>

<script>
import { mapState } from 'vuex';
import get from 'lodash/get';
import QuickResponseItem from './QuickResponseItem.vue';
import AccordionItemEditor from '@/components/AccordionItemEditor.vue';
import SearchAndCreateBox from '@/components/SearchAndCreateBox/SearchAndCreateBox.vue';
import { QUICK_RESPONSE } from '@/constants/analytics';
import { PAGINATION_DEFAULT } from '@/constants/pagination';

const SEARCH_PAGE_SIZE = 10;

export default {
  components: {
    QuickResponseItem,
    SearchAndCreateBox,
    AccordionItemEditor,
  },

  emits: [
    'selectResponse',
  ],

  async mounted() {
    await Promise.all([this.loadMyQuickResponses(), this.loadTeamQuickResponses()]);
  },

  data() {
    return {
      quickResponseEditId: null,
      quickResponseEditMode: false,
      searchQuery: '',
      selectedTeam: null,
      selectedCollapse: ['mine'],
      myQuickResponsesPagination: { ...PAGINATION_DEFAULT },
      teamQuickResponsesPagination: { ...PAGINATION_DEFAULT },
    };
  },

  computed: {
    ...mapState({
      agentProfile: state => state.agent.profile,
      myQuickResponses: state => state.conversations.quickResponses,
      teamQuickResponses: state => state.quickresponses.quickResponses,
    }),
    doMyInfiniteScroll() {
      return this.myQuickResponses.length < this.myQuickResponsesPagination.rowCount;
    },
    doTeamInfiniteScroll() {
      return this.teamQuickResponses.length < this.teamQuickResponsesPagination.rowCount;
    },
    isEditorVisible() {
      return this.quickResponseEditMode;
    },
    selectedQuickResponseItem() {
      if (this.quickResponseEditId === null) {
        return {
          id: null,
          content: '',
          is_pinned: false,
        };
      }
      return this.myQuickResponses.find(item => item.id === this.quickResponseEditId);
    },
  },

  methods: {
    deleteConfirm() {
      return new Promise(resolve => {
        this.$aiq.confirm(
          this.$t('conversation_tab.side_panel.quick_reponses_accordion.delete_confirm.title'),
          this.$t('conversation_tab.side_panel.quick_reponses_accordion.delete_confirm.message'), {
            cancelButtonText: this.$t('conversation_tab.side_panel.quick_reponses_accordion.delete_confirm.cancel_button'),
            confirmButtonText: this.$t('conversation_tab.side_panel.quick_reponses_accordion.delete_confirm.confirm_button'),
          },
        )
          .then(() => resolve(true), () => resolve(false));
      });
    },

    selectResponse(value) {
      this.$store.dispatch(
        'notifications/emitEventToSocket',
        { name: QUICK_RESPONSE.ATTACHED,
          payload: { agent: this.$store.getters['agent/profile'], quick_response_id: value.id, content: value.content, usage_type: value.usage_type},
        },
      );
      const responseWithMetadata = {
        ...value,
        _quickResponseMetadata: {
          id: value.id,
          usage_type: value.usage_type,
          is_pinned: value.is_pinned,
          message_content: value.content,
          attached_at: new Date().toISOString(),
        },
      };
      this.$emit('selectResponse', responseWithMetadata);
    },

    editResponseItem(id = null) {
      this.quickResponseEditId = id;
      this.quickResponseEditMode = true;
    },

    closeEditor() {
      this.quickResponseEditMode = false;
      this.quickResponseEditId = null;
    },

    async onSearchChange(query) {
      try {
        this.selectedCollapse = ['mine', 'team'];
        this.searchQuery = query;
        await Promise.all([this.loadMyQuickResponses(), this.loadTeamQuickResponses()]);
      } catch (ex) {
        this.$aiq.notify.error(this.$t('conversation_tab.side_panel.quick_reponses_accordion.unable_search'));
      }
    },

    async onMyScrollLoad($state) {
      await this.loadMyQuickResponses(this.myQuickResponses.length);
      if (this.myQuickResponses.length >= this.myQuickResponsesPagination.rowCount) {
        $state.complete();
        return;
      }
      $state.loaded();
    },

    async onTeamScrollLoad($state) {
      await this.loadTeamQuickResponses(this.teamQuickResponses.length);
      if (this.teamQuickResponses.length >= this.teamQuickResponsesPagination.rowCount) {
        $state.complete();
        return;
      }
      $state.loaded();
    },

    async loadMyQuickResponses(offset) {
      const params = {
        offset,
        limit: SEARCH_PAGE_SIZE,
        query: this.searchQuery,
      };
      const response = await this.$store.dispatch('conversations/getQuickResponses', params);
      this.myQuickResponsesPagination = get(response, 'data.pagination', PAGINATION_DEFAULT);
      return response;
    },

    async loadTeamQuickResponses(offset) {
      const params = {
        offset,
        limit: SEARCH_PAGE_SIZE,
        usage_types: ['all', 'teams'],
        team_id: get(this.selectedTeam, 'id'),
        agent_id: this.agentProfile.id,
        query: this.searchQuery,
      };
      const response = await this.$store.dispatch('quickresponses/getQuickresponsesList', [
        params,
        get({ offset }, 'offset', 0) === 0,
      ]);
      this.teamQuickResponsesPagination = get(response, 'data.pagination', PAGINATION_DEFAULT);
      return response;
    },

    async deleteResponse(responseItem) {
      try {
        const isConfirmed = await this.deleteConfirm();
        if (isConfirmed) {
          await this.$store.dispatch('conversations/deleteQuickResponse', responseItem.id);
          await this.loadMyQuickResponses();
          this.$aiq.notify.success(this.$t('conversation_tab.side_panel.quick_reponses_accordion.deleted_success'));
        }
      } catch (ex) {
        this.$aiq.notify.error(this.$t('conversation_tab.side_panel.quick_reponses_accordion.unable_delete'));
      }

      this.closeEditor();
    },

    async updateResponse(newRespItem) {

      const newContent = newRespItem?.content ?? '';

      if (newRespItem?.id === null) {
        // Add mode
        try {
          await this.$store.dispatch('conversations/createQuickResponse', { content: newContent, is_pinned: newRespItem.is_pinned });
          await this.loadMyQuickResponses();
          this.$aiq.notify.success(this.$t('conversation_tab.side_panel.quick_reponses_accordion.added_success'));
        } catch (ex) {
          this.$aiq.notify.error(this.$t('conversation_tab.side_panel.quick_reponses_accordion.unable_add'));
        }
      }
      else {
        // Edit mode
        const orgRespItem = this.selectedQuickResponseItem;

        if (!newRespItem?.id || !orgRespItem || this.quickResponseEditId !== newRespItem.id) {
          return;
        }

        try {
          await this.$store.dispatch('conversations/updateQuickResponse', [newRespItem.id, newContent]);
          if (newRespItem.is_pinned !== orgRespItem.is_pinned) {
            if (newRespItem.is_pinned) {
              await this.$store.dispatch('conversations/pinQuickResponse', newRespItem.id);
            }
            else {
              await this.$store.dispatch('conversations/unpinQuickResponse', newRespItem.id);
            }
          }
          await this.loadMyQuickResponses();
          this.$aiq.notify.success(this.$t('conversation_tab.side_panel.quick_reponses_accordion.updated_success'));
        } catch (ex) {
          this.$aiq.notify.error(this.$t('conversation_tab.side_panel.quick_reponses_accordion.unable_update'));
        }
      }

      this.closeEditor();
    },

    async createResponse() {
      this.quickResponseEditMode = true;
    },

    async addToMineResponses({ title, content, is_pinned }) {
      try {
        const result = await this.$store.dispatch('conversations/createQuickResponse', { title, content, is_pinned });
        if (result) {
          await this.loadMyQuickResponses();
          this.$aiq.notify.success(this.$t('conversation_tab.side_panel.quick_reponses_accordion.added_to_mine_success'));
        } else {
          this.$aiq.notify.error(this.$t('conversation_tab.side_panel.quick_reponses_accordion.already_added'));
        }
      } catch (ex) {
        this.$aiq.notify.error(this.$t('conversation_tab.side_panel.quick_reponses_accordion.unable_add_to_mine'));
      }
    },

    async pinResponse(id) {
      try {
        await this.$store.dispatch('conversations/pinQuickResponse', id);
        await this.loadMyQuickResponses();
        this.$aiq.notify.success(this.$t('conversation_tab.side_panel.quick_reponses_accordion.pinned_success'));
      } catch (ex) {
        this.$aiq.notify.error(this.$t('conversation_tab.side_panel.quick_reponses_accordion.unable_pin'));
      }
    },

    async unpinResponse(id) {
      try {
        await this.$store.dispatch('conversations/unpinQuickResponse', id);
        await this.loadMyQuickResponses();
        this.$aiq.notify.success(this.$t('conversation_tab.side_panel.quick_reponses_accordion.unpinned_success'));
      } catch (ex) {
        this.$aiq.notify.error(this.$t('conversation_tab.side_panel.quick_reponses_accordion.unable_unpin'));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .quick-response_list > .el-select {
    margin-top: 10px;
    margin-bottom: 15px;
  }

  .quick-response_search-container {
    display: flex;
    flex-wrap: wrap;
  }
  .button-add-new-item {
    flex: 0 0 0%;
    padding: 0 6px;
    color: #8492A6;
    font-size: 14px;
    border: 0;
    background-color: transparent;
  }
  .quick-response_search {
    flex: 1 0 0%;
  }
  .new-quick-response {
    width: 100%;
    margin-top: 8px;
  }
  .el-collapse.quick-response {
    margin-top: 10px;
    border: unset;
    :deep {
      .el-collapse-item__header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding-left: 5px;
        background-color: unset;
        font-weight: 700;
      }
      .el-collapse-item__wrap {
        border: unset;
        margin-bottom: 10px;
        .el-collapse-item__content {
          max-height: 420px;
          overflow-y: auto;
          height: inherit;
          padding: 0px 5px 0px 5px;
        }
      }

    }
  }
  .text-center {
    text-align: center;
    margin-top: 5px;
  }
</style>
